import { Button, Grid, OutlinedInput, Stack, Typography } from "@mui/material";
import { produce } from "immer";
import React from "react";
import { useTranslation } from "react-i18next";
import Modal, { CommonModalProps } from "..";
import { assetService, ingestService, userService, workflowService } from "../../../Core/Api/services";
import { ApproveWish } from "../../../Core/Api/Types/approve-wish";
import { DbUserAclElevationWishlistInfos } from "../../../Core/Api/Types/db-user-acl-elevation-wishlist-infos";
import { DeclineWish } from "../../../Core/Api/Types/decline-wish";
import { ObjectClass } from "../../../Core/Api/Types/object-class";
import { OperationRequestType } from "../../../Core/Api/Types/operation-request-type";
import { WorkflowType } from "../../../Core/Api/Types/workflow-type";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";

type CloseClbProps = {
  confirm: boolean;
};

export type ModalApprovalActionElementProps = {
  onClose?: (props: CloseClbProps) => void;
  approvals: DbUserAclElevationWishlistInfos[];
  operation: "Approve" | "Decline" | "ApproveAndRun" | "Run";
};
export type ModalApprovalActionProps = CommonModalProps<ModalApprovalActionElementProps>;

const ModalApprovalAction = React.forwardRef<any, ModalApprovalActionProps>((props, ref) => {
  const { closeModal, onClose, approvals, operation, ...modalProps } = props;
  const { t } = useTranslation();

  const [wishes, setWishes] = React.useState<ApproveWish[]>([]);
  const [declineWishes, setDeclineWishes] = React.useState<DeclineWish[]>([]);

  const enqueueCustomSnackbar = useCustomSnackbar();

  React.useEffect(() => {
    if (operation === "Decline")
      setDeclineWishes(
        approvals.map((x) => {
          return { id: x.id, message: null };
        })
      );
    else
      setWishes(
        approvals.map((x) => {
          return { id: x.id, maxDays: 30 };
        })
      );
  }, [approvals]);

  const onChangeMaxDays = (days: number | null, id: number) => {
    setWishes(
      produce((draft) => {
        draft!.find((x) => x.id === id)!.maxDays = days;
      })
    );
  };

  const onChangeReasonMessage = (message: string | null, id: number) => {
    setDeclineWishes(
      produce((draft) => {
        draft!.find((x) => x.id === id)!.message = message;
      })
    );
  };

  const handleCancelClick = () => {
    onClose && onClose({ confirm: false });
    closeModal();
  };

  const handleApproveClick = () => {
    userService.ApproveWishes({ requests: wishes }).then((response) => {
      if (response && response.success) {
        onClose && onClose({ confirm: true });
        closeModal();
        enqueueCustomSnackbar(t("alert:success.approvesAreSent"), "success");
      } else enqueueCustomSnackbar(t("alert:error.oopsWithMessage", { message: response.message }), "warning");
    });
  };

  const handleApproveAndRunClick = () => {
    userService.ApproveWishes({ requests: wishes }).then(function (response) {
      if (response.success) {
        enqueueCustomSnackbar(t("alert:success.approvesAreSent"), "success");
        handleRunClick();
      } else enqueueCustomSnackbar(t("alert:error.oopsWithMessage", { message: response.message }), "warning");
    });
  };

  const handleDeclineClick = () => {
    userService.DeclineWishes({ requests: declineWishes }).then((response) => {
      if (response && response.success) {
        onClose && onClose({ confirm: true });
        closeModal();
        enqueueCustomSnackbar(t("alert:success.declinesAreSent"), "success");
      } else enqueueCustomSnackbar(t("alert:error.oopsWithMessage", { message: response.message }), "warning");
    });
  };

  const handleRunClick = () => {
    approvals.forEach((approval) => {
      console.debug("Treating run of type = " + approval.type);

      switch (approval.type) {
        case OperationRequestType.PurgeAssetRequest:
          workflowService
            .CreateInstance({
              assetId: approval.requestInfos!.objectId,
              definitionId: null,
              definitionType: WorkflowType.PurgeMediaFile,
              annotation: approval.annotation,
              inboundVars: approval.requestInfos!.workflowInboundVars,
              aclElevationId: approval.id,
            })
            .then((response) => {
              apiCallback(response.success!, response.message);
            });
          break;
        case OperationRequestType.DeleteAssetRequest:
          ingestService
            .DeleteRequest({
              comment: approval.annotation,
              id: approval.requestInfos!.objectId,
              aclElevationId: approval.id,
            })
            .then((response) => {
              apiCallback(response.success!, response.message);
            });
          break;
        case OperationRequestType.BypassAssetRejection:
          workflowService
            .CreateInstance({
              assetId: approval.requestInfos!.objectId,
              definitionId: null,
              definitionType: WorkflowType.BypassRejection,
              annotation: approval.annotation,
              aclElevationId: approval.id,
            })
            .then((response) => {
              apiCallback(response.success!, response.message);
            });
          break;
        case OperationRequestType.MoveAsset:
          if (approval.requestInfos?.objectId && approval.requestInfos?.additionalObjectId)
            assetService
              .MoveObjectAsync({
                objectIds: [approval.requestInfos.objectId],
                targetParentObjectId: approval.requestInfos.additionalObjectId,
                aclElevationId: approval.id,
              })
              .then((response) => {
                apiCallback(response.success!, response.message);
              });
          break;
        case OperationRequestType.CopyAsset:
          if (approval.requestInfos?.objectId && approval.requestInfos?.additionalObjectId)
            assetService
              .CopyObjectAsync({
                objectIds: [approval.requestInfos.objectId],
                targetParentObjectId: approval.requestInfos?.additionalObjectId,
                providerId: null,
                overridePlaceholderTemplateId: undefined,
                aclElevationId: approval.id,
              })
              .then((response) => {
                apiCallback(response.success!, response.message);
              });
          break;

        case OperationRequestType.SwitchClassToMezz:
          if (approval.requestInfos?.objectId)
            assetService
              .SwitchObjectClass({
                annotation: approval.annotation,
                assetId: approval.requestInfos.objectId,
                classification: ObjectClass.Mezz,
                aclElevationId: approval.id,
              })
              .then((response) => {
                apiCallback(response.success!, response.message);
              });
          break;
        case OperationRequestType.SwitchClassToRaw:
          if (approval.requestInfos?.objectId)
            assetService
              .SwitchObjectClass({
                annotation: approval.annotation,
                assetId: approval.requestInfos.objectId,
                classification: ObjectClass.Raw,
                aclElevationId: approval.id,
              })
              .then((response) => {
                apiCallback(response.success!, response.message);
              });
          break;
      }
    });
  };

  const apiCallback = (success: boolean, message: string | null) => {
    if (success) {
      onClose && onClose({ confirm: true });
      closeModal();
      enqueueCustomSnackbar(t("alert:success.operationStarted"), "success");
    } else {
      enqueueCustomSnackbar(t("alert:error.oopsWithMessage", { message }), "warning");
    }
  };

  return (
    <Modal
      {...modalProps}
      title={operation}
      reference={ref}
      closeModal={handleCancelClick}
      width={operation !== "Run" ? 750 : 400}
      footer={
        <Stack direction="row" justifyContent="space-between">
          <Button onClick={handleCancelClick}>{t("common:cancel")}</Button>
          <Stack direction="row" spacing={1}>
            {operation === "Approve" && (
              <>
                <Button variant="contained" color="primary" onClick={handleApproveClick}>
                  {t("button:approve")}
                </Button>
              </>
            )}
            {operation === "Decline" && (
              <>
                <Button variant="contained" color="primary" onClick={handleDeclineClick}>
                  {t("common:decline")}
                </Button>
              </>
            )}
            {operation === "ApproveAndRun" && (
              <>
                <Button variant="contained" color="primary" onClick={handleApproveAndRunClick}>
                  {t("button:approveAndRun")}
                </Button>
              </>
            )}
            {operation === "Run" && (
              <>
                <Button variant="contained" color="primary" onClick={handleRunClick}>
                  {t("button:run")}
                </Button>
              </>
            )}
          </Stack>
        </Stack>
      }
    >
      <Stack spacing={2}>
        <Grid container>
          <Grid item xs={6} zeroMinWidth>
            <Typography>{t("common:title")}</Typography>
          </Grid>
          <Grid item xs={6} zeroMinWidth>
            {operation === "Decline" && <Typography>{t("modal:body.reasonMessage")}</Typography>}
            {operation === "Approve" && (
              <Typography style={{ overflowWrap: "break-word" }}>
                {t("modal:modalApprovalAction.approveText")}
              </Typography>
            )}
          </Grid>
        </Grid>
        <Stack spacing={2}>
          {approvals.map((approval) => (
            <Stack spacing={2} key={approval.id} direction="row">
              <Stack width={"100%"}>
                <OutlinedInput disabled value={approval.titleName} />
              </Stack>

              {operation === "Approve" && (
                <Stack width={"100%"}>
                  <OutlinedInput
                    value={wishes.find((x) => x.id === approval.id)?.maxDays || ""}
                    onChange={(e) => onChangeMaxDays(parseInt(e.target.value) || null, approval.id)}
                    type="number"
                  />
                </Stack>
              )}
              {operation === "Decline" && (
                <Stack width={"100%"}>
                  <OutlinedInput
                    value={declineWishes.find((x) => x.id === approval.id)?.message || ""}
                    onChange={(e) => onChangeReasonMessage(e.target.value || null, approval.id)}
                  />
                </Stack>
              )}
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Modal>
  );
});

export default ModalApprovalAction;
